// EventSource subscription for `/api/workers/:hostname/live`.
//
// The native `EventSource` already retries when the connection drops, but
// it has no way to give up gracefully when the page unmounts mid-flight,
// so we own the lifecycle here. Backoff is light: native retry handles
// the common cases, we only add a manual reconnect on hard errors.

import { useEffect, useRef, useState } from "react";

export interface FleetLiveCpuSlice {
	usrPct: number;
	nicePct: number;
	sysPct: number;
	iowaitPct: number;
	irqPct: number;
	softirqPct: number;
	stealPct: number;
	idlePct: number;
	totalPct: number;
}

export interface FleetLiveCpu {
	aggregate: FleetLiveCpuSlice;
	perCore?: Array<FleetLiveCpuSlice & { cpu: number }>;
	source: "proc" | "os";
}

/** Raw /proc/meminfo KB values as emitted by the worker's /fleet/live endpoint. */
export interface FleetLiveMem {
	totalKB: number;
	freeKB: number;
	availableKB: number;
	buffersKB: number;
	cachedKB: number;
	sReclaimableKB: number;
	shmemKB: number;
	swapTotalKB: number;
	swapFreeKB: number;
	source: "proc" | "os";
}

export interface FleetLiveDisplayInfo {
	display: number;
	pid: number;
	adopted: boolean;
	sessions: number;
}

export interface FleetLiveDisplays {
	disabled: boolean;
	displays: FleetLiveDisplayInfo[];
}

export interface FleetLiveOk {
	ok: true;
	ts: string;
	hostname: string;
	cpu?: FleetLiveCpu;
	mem?: FleetLiveMem;
	loadAvg1m?: number;
	loadAvg5m?: number;
	loadAvg15m?: number;
	activeSessions?: number;
	displays?: FleetLiveDisplays;
}

export interface FleetLiveErr {
	ok: false;
	ts: string;
	hostname: string;
	reason: string;
}

export type FleetLiveEvent = FleetLiveOk | FleetLiveErr;

export interface UseFleetLiveResult {
	live: FleetLiveOk | null;
	connected: boolean;
	error: string | null;
}

/**
 * Subscribe to live host metrics over SSE. The hook returns the most
 * recent successful payload, the live connection state, and the latest
 * error reason if the worker is unreachable. `connected` flips to false
 * when the stream closes or the most recent event was an error.
 */
export function useFleetLive(
	hostname: string | null | undefined,
): UseFleetLiveResult {
	const [live, setLive] = useState<FleetLiveOk | null>(null);
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const sourceRef = useRef<EventSource | null>(null);

	useEffect(() => {
		if (!hostname) {
			setLive(null);
			setConnected(false);
			setError(null);
			return;
		}

		let cancelled = false;
		let retryTimer: ReturnType<typeof setTimeout> | null = null;
		let attempt = 0;

		const open = () => {
			if (cancelled) return;
			const es = new EventSource(
				`/api/workers/${encodeURIComponent(hostname)}/live`,
				{ withCredentials: true },
			);
			sourceRef.current = es;

			es.onopen = () => {
				attempt = 0;
				setConnected(true);
				setError(null);
			};
			es.onmessage = (msg) => {
				try {
					const ev = JSON.parse(msg.data) as FleetLiveEvent;
					if (ev.ok) {
						setLive(ev);
						setConnected(true);
						setError(null);
					} else {
						setConnected(false);
						setError(ev.reason);
					}
				} catch {
					// Ignore malformed payloads — the next event will overwrite.
				}
			};
			es.onerror = () => {
				setConnected(false);
				es.close();
				if (cancelled) return;
				attempt = Math.min(attempt + 1, 5);
				const delay = Math.min(10_000, 1_000 * 2 ** (attempt - 1));
				retryTimer = setTimeout(open, delay);
			};
		};

		open();

		return () => {
			cancelled = true;
			if (retryTimer) clearTimeout(retryTimer);
			sourceRef.current?.close();
			sourceRef.current = null;
		};
	}, [hostname]);

	return { live, connected, error };
}
